import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, getCollectionName } from './firebase';
import {
  collection, query, onSnapshot, orderBy, doc,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from './firestore-compat';

// ── Constantes ────────────────────────────────────────────────────────────────
const STATUS_GERAL_OPTS = ['EM ATENDIMENTO', 'ALTA'];
const STATUS_GERAL_COLOR = {
  'EM ATENDIMENTO': { bg: '#dbeafe', text: '#1d4ed8' },
  'ALTA':           { bg: '#dcfce7', text: '#15803d' },
};

const P = {
  bg:'#f8fafc', white:'#fff', border:'#e2e8f0', text:'#1e293b',
  muted:'#64748b', primary:'#6366f1', green:'#22c55e',
  red:'#ef4444', amber:'#f59e0b', blue:'#3b82f6', gray:'#94a3b8',
};

// Ordem exata das colunas na planilha de origem (A→S) — usada pela importação de CSV
const CSV_COLUMN_ORDER = [
  'status', 'nro_atendimento', 'paciente', 'seguradora', 'tentativa1_data',
  'contato_realizado', 'prontuario_logistica', 'tentativa2_data', 'tentativa3_data',
  'observacao', 'entregue_faturamento', 'data_entrega_faturamento', 'observacao_faturamento',
  'endereco', 'numero_complemento', 'bairro', 'cidade', 'cod_zona', 'contato',
];

const EMPTY = {
  status: 'EM ATENDIMENTO',
  nro_atendimento: '',
  paciente: '',
  seguradora: '',
  tentativa1_data: '',
  contato_realizado: false,
  prontuario_logistica: false,
  tentativa2_data: '',
  tentativa3_data: '',
  observacao: '',
  entregue_faturamento: false,
  data_entrega_faturamento: '',
  observacao_faturamento: '',
  endereco: '',
  numero_complemento: '',
  bairro: '',
  cidade: '',
  cod_zona: '',
  contato: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(s) {
  if (!s || typeof s !== 'string') return '—';
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '—';
  return `${match[3]}/${match[2]}/${match[1]}`;
}
// Converte "DD/MM/AAAA" (formato da planilha) para "AAAA-MM-DD" (formato do <input type="date">)
function parseDateBR(s) {
  if (!s || typeof s !== 'string') return '';
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return '';
  const [, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
function parseBool(s) {
  if (typeof s === 'boolean') return s;
  const v = String(s || '').trim().toUpperCase();
  return v === 'TRUE' || v === 'VERDADEIRO' || v === 'SIM' || v === '1' || v === 'X';
}

// ── Mini-componentes ────────────────────────────────────────────────────────────
function KpiCard({ label, value, color, sub }) {
  return (
    <div style={{ background:P.white, border:`1px solid ${P.border}`, borderRadius:10,
      padding:'12px 18px', minWidth:150, flex:1 }}>
      <div style={{ fontSize:'0.68rem', fontWeight:700, color:P.muted, textTransform:'uppercase', letterSpacing:.5 }}>{label}</div>
      <div style={{ fontSize:'1.5rem', fontWeight:800, color, lineHeight:1.2, margin:'4px 0 0' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.68rem', color:P.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:P.white, borderRadius:14, width:'100%', maxWidth:wide?860:560,
        maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'16px 22px', borderBottom:`1px solid ${P.border}`,
          position:'sticky', top:0, background:P.white, zIndex:1 }}>
          <h2 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:P.text }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.3rem', cursor:'pointer', color:P.muted }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:'0.71rem', fontWeight:700, color:P.muted,
        textTransform:'uppercase', letterSpacing:.5, marginBottom:5 }}>
        {label}{required && <span style={{ color:P.red }}> *</span>}
      </label>
      {children}
    </div>
  );
}
const inp = {
  width:'100%', padding:'8px 11px', border:`1px solid ${P.border}`, borderRadius:8,
  fontSize:'0.85rem', background:P.white, color:P.text, boxSizing:'border-box', outline:'none',
};
function Sel({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function Btn({ children, onClick, disabled, color = P.primary, small }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:disabled?P.border:color, color:'#fff', border:'none',
        borderRadius:8, padding:small?'5px 12px':'8px 18px',
        fontWeight:700, fontSize:small?'0.78rem':'0.84rem',
        cursor:disabled?'default':'pointer', opacity:disabled?.6:1, whiteSpace:'nowrap' }}>
      {children}
    </button>
  );
}
function GhostBtn({ children, onClick, small }) {
  return (
    <button onClick={onClick}
      style={{ background:'none', border:`1px solid ${P.border}`, color:P.muted,
        borderRadius:8, padding:small?'4px 10px':'7px 14px',
        fontWeight:600, fontSize:small?'0.78rem':'0.82rem', cursor:'pointer', whiteSpace:'nowrap' }}>
      {children}
    </button>
  );
}
function TH({ children }) {
  return <th style={{ padding:'9px 10px', textAlign:'left', fontSize:'0.66rem', fontWeight:700,
    color:P.muted, textTransform:'uppercase', letterSpacing:.4, borderBottom:`1px solid ${P.border}`,
    whiteSpace:'nowrap', background:'#f8fafc' }}>{children}</th>;
}
function StatusBadge({ status }) {
  const c = STATUS_GERAL_COLOR[status] || { bg:'#f1f5f9', text:'#64748b' };
  return (
    <span style={{ background:c.bg, color:c.text, padding:'2px 9px', borderRadius:20,
      fontWeight:700, fontSize:'0.7rem', whiteSpace:'nowrap' }}>{status || '—'}</span>
  );
}
function BoolPill({ value, trueLabel = 'Sim', falseLabel = 'Não' }) {
  return (
    <span style={{ fontWeight:700, fontSize:'0.7rem', color: value ? '#15803d' : P.gray }}>
      {value ? `✓ ${trueLabel}` : falseLabel}
    </span>
  );
}
// Cor da linha inteira, por prioridade: Alta > Entregue ao faturamento (completo) > Pendente
function rowVisual(r) {
  if (r.status === 'ALTA') return { bg: '#eff6ff', label: 'Alta', dot: '#3b82f6' };
  if (r.entregue_faturamento) return { bg: '#f0fdf4', label: 'Completo', dot: '#22c55e' };
  return { bg: '#fef2f2', label: 'Pendente faturamento', dot: '#ef4444' };
}
function DelModal({ onClose, onConfirm }) {
  return (
    <Modal title="Confirmar exclusão" onClose={onClose}>
      <p style={{ color:P.text, marginBottom:20 }}>Deseja excluir este registro? Esta ação não pode ser desfeita.</p>
      <div style={{ display:'flex', gap:10 }}>
        <Btn color={P.red} onClick={onConfirm}>Excluir</Btn>
        <GhostBtn onClick={onClose}>Cancelar</GhostBtn>
      </div>
    </Modal>
  );
}

// ── Importação de CSV (export do Google Sheets) ────────────────────────────────
// Muitos exports salvos no Windows usam Windows-1252/ISO-8859-1 em vez de UTF-8.
// Forçar UTF-8 corrompe acentos ("�"); aqui detectamos e caímos para Windows-1252.
function decodeFileText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => {
      const buffer = ev.target.result;
      let text;
      try { text = new TextDecoder('utf-8', { fatal: true }).decode(buffer); }
      catch { text = new TextDecoder('windows-1252').decode(buffer); }
      resolve(text);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return { header: [], rows: [] };
  const sep = lines[0].includes(';') ? ';' : ',';
  const parseRow = l => l.split(sep).map(v => v.replace(/^"|"$/g, '').trim());
  return { header: parseRow(lines[0]), rows: lines.slice(1).map(parseRow) };
}
function rowToRecord(cols) {
  const rec = { ...EMPTY };
  CSV_COLUMN_ORDER.forEach((field, i) => {
    const raw = (cols[i] ?? '').trim();
    if (field.endsWith('_data')) rec[field] = parseDateBR(raw);
    else if (['contato_realizado', 'prontuario_logistica', 'entregue_faturamento'].includes(field)) rec[field] = parseBool(raw);
    else if (field === 'status') rec[field] = STATUS_GERAL_OPTS.includes(raw.toUpperCase()) ? raw.toUpperCase() : (raw || 'EM ATENDIMENTO');
    else rec[field] = raw;
  });
  return rec;
}

function ImportModal({ col, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleFile = async e => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const text = await decodeFileText(f);
    const { rows: r } = parseCSV(text);
    const records = r
      .map(rowToRecord)
      .filter(rec => rec.nro_atendimento || rec.paciente);
    setRows(records);
  };

  const runImport = async () => {
    if (rows.length === 0) return;
    setImporting(true);
    setProgress(0);
    const CHUNK = 25;
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        await Promise.all(chunk.map(rec => addDoc(collection(db, col), { ...rec, criadoEm: serverTimestamp() })));
        setProgress(Math.min(i + CHUNK, rows.length));
      }
      onImported(rows.length);
    } catch (e) {
      alert('Erro durante a importação: ' + (e.message || e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal title="📥 Importar Planilha (CSV)" onClose={onClose} wide>
      <p style={{ color:P.muted, fontSize:'0.82rem', marginBottom:16 }}>
        No Google Sheets, use <strong>Arquivo → Fazer download → Valores separados por vírgula (.csv)</strong> e selecione o arquivo abaixo.
        O import segue a ordem fixa de colunas da planilha original (Status, Nº Atendimento, Paciente, Seguradora, 1ª/2ª/3ª Tentativa, Prontuário na Logística, Observações, Faturamento, Endereço…) — confira a pré-visualização antes de confirmar.
      </p>
      <label style={{
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        border:'2px dashed #e2e8f0', borderRadius:12, padding:'28px 24px', cursor:'pointer',
        background: file ? '#f0fdf4' : '#f8fafc', marginBottom:16,
      }}>
        <input ref={inputRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleFile} />
        <span style={{ fontSize:32, marginBottom:6 }}>{file ? '✅' : '📂'}</span>
        <span style={{ fontWeight:700, color: file ? '#16a34a' : P.primary, fontSize:'0.88rem' }}>
          {file ? file.name : 'Clique para selecionar o arquivo CSV'}
        </span>
      </label>

      {rows.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, color:P.text, fontSize:'0.82rem', marginBottom:8 }}>
            {rows.length} registros detectados — pré-visualização (5 primeiros):
          </div>
          <div style={{ overflowX:'auto', border:`1px solid ${P.border}`, borderRadius:8 }}>
            <table style={{ borderCollapse:'collapse', fontSize:'0.72rem', width:'100%' }}>
              <thead><tr>
                <TH>Status</TH><TH>Nro</TH><TH>Paciente</TH><TH>Seguradora</TH><TH>Cidade</TH><TH>Zona</TH>
              </tr></thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={i} style={{ borderBottom:`1px solid #f1f5f9` }}>
                    <td style={{ padding:'6px 10px' }}><StatusBadge status={r.status} /></td>
                    <td style={{ padding:'6px 10px' }}>{r.nro_atendimento || '—'}</td>
                    <td style={{ padding:'6px 10px' }}>{r.paciente || '—'}</td>
                    <td style={{ padding:'6px 10px' }}>{r.seguradora || '—'}</td>
                    <td style={{ padding:'6px 10px' }}>{r.cidade || '—'}</td>
                    <td style={{ padding:'6px 10px' }}>{r.cod_zona || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {importing && (
        <div style={{ marginBottom:14, color:P.primary, fontWeight:700, fontSize:'0.82rem' }}>
          ⏳ Importando… {progress}/{rows.length}
        </div>
      )}

      <div style={{ display:'flex', gap:10 }}>
        <Btn onClick={runImport} disabled={rows.length === 0 || importing}>
          {importing ? 'Importando…' : `Importar ${rows.length || ''} registro${rows.length !== 1 ? 's' : ''}`}
        </Btn>
        <GhostBtn onClick={onClose}>Cancelar</GhostBtn>
      </div>
    </Modal>
  );
}

// ── Modal de Cadastro/Edição ────────────────────────────────────────────────────
function RegistroModal({ edit, seguradoras, zonas, onClose, onSave }) {
  const [form, setForm] = useState(edit ? { ...EMPTY, ...edit } : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.paciente.trim()) return;
    setSaving(true);
    try { await onSave(form); }
    catch (e) { alert('Erro ao salvar: ' + (e.message || e)); }
    setSaving(false);
  };

  return (
    <Modal title={edit ? 'Editar Registro' : 'Novo Registro'} onClose={onClose} wide>
      <datalist id="lg-seguradoras">{seguradoras.map(s => <option key={s} value={s} />)}</datalist>
      <datalist id="lg-zonas">{zonas.map(z => <option key={z} value={z} />)}</datalist>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 18px' }}>
        <div style={{ gridColumn:'1/-1', fontWeight:800, color:P.primary, fontSize:'0.78rem', textTransform:'uppercase', letterSpacing:.5, margin:'2px 0 8px' }}>
          Paciente
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Nome do paciente" required>
            <input value={form.paciente} onChange={e => set('paciente', e.target.value)} placeholder="Nome completo" style={inp} />
          </Field>
        </div>
        <Field label="Nº de atendimento">
          <input value={form.nro_atendimento} onChange={e => set('nro_atendimento', e.target.value)} placeholder="Ex.: 3344" style={inp} />
        </Field>
        <Field label="Status"><Sel value={form.status} onChange={v => set('status', v)} options={STATUS_GERAL_OPTS} /></Field>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Seguradora">
            <input value={form.seguradora} onChange={e => set('seguradora', e.target.value)} placeholder="Ex.: HB - UNIMED BRASIL" style={inp} list="lg-seguradoras" />
          </Field>
        </div>

        <div style={{ gridColumn:'1/-1', fontWeight:800, color:P.primary, fontSize:'0.78rem', textTransform:'uppercase', letterSpacing:.5, margin:'10px 0 8px', borderTop:`1px solid ${P.border}`, paddingTop:14 }}>
          Coleta do Prontuário
        </div>
        <Field label="1ª tentativa"><input type="date" value={form.tentativa1_data} onChange={e => set('tentativa1_data', e.target.value)} style={inp} /></Field>
        <Field label="2ª tentativa"><input type="date" value={form.tentativa2_data} onChange={e => set('tentativa2_data', e.target.value)} style={inp} /></Field>
        <Field label="3ª tentativa"><input type="date" value={form.tentativa3_data} onChange={e => set('tentativa3_data', e.target.value)} style={inp} /></Field>
        <div />
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Situação da coleta">
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {[
                { key:'contato_realizado',    label:'Contato realizado com sucesso' },
                { key:'prontuario_logistica', label:'Prontuário na logística' },
              ].map(({ key, label }) => (
                <label key={key} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer',
                  background: form[key] ? '#dcfce7' : '#f1f5f9',
                  border:`1px solid ${form[key] ? '#86efac' : P.border}`,
                  borderRadius:8, padding:'6px 12px', fontSize:'0.8rem', fontWeight:600,
                  color: form[key] ? '#15803d' : P.muted }}>
                  <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} style={{ accentColor:'#15803d' }} />
                  {label}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Observação (coleta)">
            <textarea value={form.observacao} onChange={e => set('observacao', e.target.value)} rows={2} style={{ ...inp, resize:'vertical' }} />
          </Field>
        </div>

        <div style={{ gridColumn:'1/-1', fontWeight:800, color:P.primary, fontSize:'0.78rem', textTransform:'uppercase', letterSpacing:.5, margin:'10px 0 8px', borderTop:`1px solid ${P.border}`, paddingTop:14 }}>
          Faturamento
        </div>
        <Field label="Entregue ao faturamento">
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 0' }}>
            <input type="checkbox" checked={!!form.entregue_faturamento} onChange={e => set('entregue_faturamento', e.target.checked)} style={{ accentColor:'#15803d' }} />
            <span style={{ fontSize:'0.85rem', color:P.text }}>{form.entregue_faturamento ? 'Sim' : 'Não'}</span>
          </label>
        </Field>
        <Field label="Data de entrega"><input type="date" value={form.data_entrega_faturamento} onChange={e => set('data_entrega_faturamento', e.target.value)} style={inp} /></Field>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Observação (faturamento)">
            <textarea value={form.observacao_faturamento} onChange={e => set('observacao_faturamento', e.target.value)} rows={2} style={{ ...inp, resize:'vertical' }} />
          </Field>
        </div>

        <div style={{ gridColumn:'1/-1', fontWeight:800, color:P.primary, fontSize:'0.78rem', textTransform:'uppercase', letterSpacing:.5, margin:'10px 0 8px', borderTop:`1px solid ${P.border}`, paddingTop:14 }}>
          Endereço de Coleta
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Endereço">
            <input value={form.endereco} onChange={e => set('endereco', e.target.value)} placeholder="Rua, avenida, estrada…" style={inp} />
          </Field>
        </div>
        <Field label="Nº / Complemento"><input value={form.numero_complemento} onChange={e => set('numero_complemento', e.target.value)} style={inp} /></Field>
        <Field label="Bairro"><input value={form.bairro} onChange={e => set('bairro', e.target.value)} style={inp} /></Field>
        <Field label="Cidade"><input value={form.cidade} onChange={e => set('cidade', e.target.value)} style={inp} /></Field>
        <Field label="Cód. Zona">
          <input value={form.cod_zona} onChange={e => set('cod_zona', e.target.value)} placeholder="Ex.: Norte 02" style={inp} list="lg-zonas" />
        </Field>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Contato">
            <input value={form.contato} onChange={e => set('contato', e.target.value)} placeholder="Ex.: Tel Celular (21) 99999-9999 — mãe" style={inp} />
          </Field>
        </div>
      </div>

      <div style={{ display:'flex', gap:10, marginTop:8 }}>
        <Btn onClick={save} disabled={saving || !form.paciente.trim()}>{saving ? 'Salvando…' : 'Salvar registro'}</Btn>
        <GhostBtn onClick={onClose}>Cancelar</GhostBtn>
      </div>
    </Modal>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GestaoLogistica() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fSeguradora, setFSeguradora] = useState('');
  const [fCidade, setFCidade] = useState('');
  const [fZona, setFZona] = useState('');
  const [fEntregue, setFEntregue] = useState('');
  const [fDataDe, setFDataDe] = useState('');
  const [fDataAte, setFDataAte] = useState('');
  const [modal, setModal] = useState(false);
  const [edit, setEdit] = useState(null);
  const [delId, setDelId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const col = getCollectionName('logistica_prontuarios');

  useEffect(() => {
    const q = query(collection(db, col), orderBy('criadoEm', 'desc'));
    const unsub = onSnapshot(q, snap => setData(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [col]);

  const seguradoras = useMemo(() => [...new Set(data.map(r => r.seguradora).filter(Boolean))].sort(), [data]);
  const cidades     = useMemo(() => [...new Set(data.map(r => r.cidade).filter(Boolean))].sort(), [data]);
  const zonas       = useMemo(() => [...new Set(data.map(r => r.cod_zona).filter(Boolean))].sort(), [data]);

  const rows = useMemo(() => {
    let r = data;
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(x => ((x.paciente||'') + (x.nro_atendimento||'') + (x.endereco||'')).toLowerCase().includes(s));
    }
    if (fStatus)     r = r.filter(x => x.status === fStatus);
    if (fSeguradora) r = r.filter(x => x.seguradora === fSeguradora);
    if (fCidade)     r = r.filter(x => x.cidade === fCidade);
    if (fZona)       r = r.filter(x => x.cod_zona === fZona);
    if (fEntregue)   r = r.filter(x => (fEntregue === 'sim') === !!x.entregue_faturamento);
    if (fDataDe)     r = r.filter(x => x.tentativa1_data && x.tentativa1_data >= fDataDe);
    if (fDataAte)    r = r.filter(x => x.tentativa1_data && x.tentativa1_data <= fDataAte);
    return r;
  }, [data, search, fStatus, fSeguradora, fCidade, fZona, fEntregue, fDataDe, fDataAte]);

  const kpis = useMemo(() => ({
    total: data.length,
    emAtendimento: data.filter(x => x.status === 'EM ATENDIMENTO').length,
    coletados: data.filter(x => x.prontuario_logistica).length,
    entregues: data.filter(x => x.entregue_faturamento).length,
    aguardando: data.filter(x => x.status === 'EM ATENDIMENTO' && !x.prontuario_logistica).length,
  }), [data]);

  const hasF = search || fStatus || fSeguradora || fCidade || fZona || fEntregue || fDataDe || fDataAte;
  const clrF = () => { setSearch(''); setFStatus(''); setFSeguradora(''); setFCidade(''); setFZona(''); setFEntregue(''); setFDataDe(''); setFDataAte(''); };

  const openNew  = () => { setEdit(null); setModal(true); };
  const openEdit = r => { setEdit(r); setModal(true); };
  const close    = () => { setModal(false); setEdit(null); };

  const handleSave = async form => {
    if (edit) await updateDoc(doc(db, col, edit.id), { ...form, updatedAt: serverTimestamp() });
    else      await addDoc(collection(db, col), { ...form, criadoEm: serverTimestamp() });
    close();
  };

  return (
    <div style={{ background:P.bg, minHeight:'100vh', padding:'24px 24px 60px' }}>
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:'1.4rem', fontWeight:800, color:P.text }}>📦 Gestão da Logística</h1>
          <p style={{ margin:'4px 0 0', color:P.muted, fontSize:'0.83rem' }}>Controle de coleta de prontuário físico do paciente e entrega ao faturamento</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <GhostBtn onClick={() => setImportOpen(true)}>📥 Importar Planilha</GhostBtn>
          <Btn onClick={openNew}>+ Novo Registro</Btn>
        </div>
      </div>

      <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <KpiCard label="Total"             value={kpis.total}        color={P.muted} />
        <KpiCard label="Em Atendimento"    value={kpis.emAtendimento} color={P.blue} />
        <KpiCard label="Prontuário Coletado" value={kpis.coletados}  color={P.green} />
        <KpiCard label="Entregues ao Faturamento" value={kpis.entregues} color="#15803d" />
        <KpiCard label="Aguardando Coleta" value={kpis.aguardando}   color={P.amber} />
      </div>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:14 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar paciente, nº atendimento ou endereço…"
          style={{ ...inp, maxWidth:280, flex:1 }} />
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todos os status</option>
          {STATUS_GERAL_OPTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={fSeguradora} onChange={e => setFSeguradora(e.target.value)} style={{ ...inp, maxWidth:190 }}>
          <option value="">Todas seguradoras</option>
          {seguradoras.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={fCidade} onChange={e => setFCidade(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Todas cidades</option>
          {cidades.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={fZona} onChange={e => setFZona(e.target.value)} style={{ ...inp, maxWidth:150 }}>
          <option value="">Todas zonas</option>
          {zonas.map(z => <option key={z}>{z}</option>)}
        </select>
        <select value={fEntregue} onChange={e => setFEntregue(e.target.value)} style={{ ...inp, maxWidth:170 }}>
          <option value="">Faturamento: todos</option>
          <option value="sim">Entregue</option>
          <option value="nao">Pendente</option>
        </select>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:'0.7rem', color:P.muted, whiteSpace:'nowrap' }}>1ª tentativa:</span>
          <input type="date" value={fDataDe} onChange={e => setFDataDe(e.target.value)} style={{ ...inp, maxWidth:148 }} title="De" />
          <input type="date" value={fDataAte} onChange={e => setFDataAte(e.target.value)} style={{ ...inp, maxWidth:148 }} title="Até" />
        </div>
        {hasF && <GhostBtn onClick={clrF}>✕ Limpar</GhostBtn>}
        <span style={{ marginLeft:'auto', fontSize:'0.75rem', color:P.muted }}>{rows.length} registro{rows.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:10, fontSize:'0.72rem', color:P.muted }}>
        <span style={{ fontWeight:700 }}>Legenda:</span>
        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:3, background:'#3b82f6', marginRight:5, verticalAlign:'middle' }} />Alta</span>
        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:3, background:'#22c55e', marginRight:5, verticalAlign:'middle' }} />Completo (entregue ao faturamento)</span>
        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:3, background:'#ef4444', marginRight:5, verticalAlign:'middle' }} />Pendente no faturamento</span>
      </div>

      <div style={{ background:P.white, borderRadius:12, border:`1px solid ${P.border}`, overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:'0.76rem', minWidth:1100 }}>
          <thead><tr>
            <TH>Nº / Paciente</TH><TH>Seguradora</TH><TH>Status</TH>
            <TH>Tentativas</TH><TH>Contato</TH><TH>Prontuário</TH>
            <TH>Faturamento</TH><TH>Endereço</TH><TH>Contato Paciente</TH><TH>Ações</TH>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} style={{ padding:32, textAlign:'center', color:P.muted }}>Nenhum registro.</td></tr>}
            {rows.map((r, i) => {
              const rv = rowVisual(r);
              return (
              <tr key={r.id} style={{ borderBottom:`1px solid #f1f5f9`, background: rv.bg }}>
                <td style={{ padding:'7px 10px' }}>
                  <div style={{ fontWeight:700, color:P.text }}>{r.paciente || '—'}</div>
                  <div style={{ color:P.muted, fontSize:'0.68rem' }}>Nº {r.nro_atendimento || '—'}</div>
                </td>
                <td style={{ padding:'7px 10px', color:P.text, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.seguradora}>{r.seguradora || '—'}</td>
                <td style={{ padding:'7px 10px' }}><StatusBadge status={r.status} /></td>
                <td style={{ padding:'7px 10px', color:P.muted, fontSize:'0.7rem', whiteSpace:'nowrap' }}>
                  <div>1ª: {fmtDate(r.tentativa1_data)}</div>
                  <div>2ª: {fmtDate(r.tentativa2_data)}</div>
                  <div>3ª: {fmtDate(r.tentativa3_data)}</div>
                </td>
                <td style={{ padding:'7px 10px' }}><BoolPill value={r.contato_realizado} /></td>
                <td style={{ padding:'7px 10px' }}><BoolPill value={r.prontuario_logistica} trueLabel="Na logística" falseLabel="Aguardando" /></td>
                <td style={{ padding:'7px 10px' }}>
                  <BoolPill value={r.entregue_faturamento} trueLabel="Entregue" falseLabel="Pendente" />
                  {r.entregue_faturamento && <div style={{ color:P.muted, fontSize:'0.68rem' }}>{fmtDate(r.data_entrega_faturamento)}</div>}
                </td>
                <td style={{ padding:'7px 10px', color:P.text, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={[r.endereco, r.numero_complemento, r.bairro, r.cidade, r.cod_zona].filter(Boolean).join(' · ')}>
                  <div style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{r.endereco || '—'}{r.numero_complemento ? `, ${r.numero_complemento}` : ''}</div>
                  <div style={{ color:P.muted, fontSize:'0.68rem' }}>{[r.bairro, r.cidade, r.cod_zona].filter(Boolean).join(' · ')}</div>
                </td>
                <td style={{ padding:'7px 10px', color:P.text, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.contato}>{r.contato || '—'}</td>
                <td style={{ padding:'7px 10px', whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <GhostBtn small onClick={() => openEdit(r)}>✏️</GhostBtn>
                    <GhostBtn small onClick={() => setDelId(r.id)}>🗑</GhostBtn>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <RegistroModal edit={edit} seguradoras={seguradoras} zonas={zonas} onClose={close} onSave={handleSave} />
      )}
      {importOpen && (
        <ImportModal col={col} onClose={() => setImportOpen(false)} onImported={n => { setImportOpen(false); alert(`${n} registros importados com sucesso!`); }} />
      )}
      {delId && (
        <DelModal onClose={() => setDelId(null)} onConfirm={() => { deleteDoc(doc(db, col, delId)); setDelId(null); }} />
      )}
    </div>
  );
}
